import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Edit2, Check, X, MessageSquare, Clock } from 'lucide-react';
import { API } from '../config/api';

// ---------- helpers ---------------------------------------------------------

const formatTime = (s) => {
  if (s == null || !Number.isFinite(s)) return '—';
  const sign = s < 0 ? '-' : '';
  const abs = Math.abs(s);
  const mm = Math.floor(abs / 60);
  const ss = abs - mm * 60;
  return `${sign}${String(mm).padStart(2, '0')}:${ss.toFixed(3).padStart(6, '0')}`;
};

// Resolve the most-useful temporal anchor of an annotation. Notes created
// from DAQ have only `distance` while notes from Replay have only `time_s`
// (with distance back-filled on best-effort). This helper unifies both
// worlds so the Replay timeline + the DAQ list can ALWAYS jump to the
// note regardless of which side created it.
//
// Lookup order:
//   1. `ann.time_s` (already the value we want)
//   2. binary-search `ann.distance` against the lap's `distance_m` samples
//      and read the matching `time_s` — exact for the lap that was open
//      when the note was saved.
//   3. distance/avg_speed fallback (very rough — only used if the lap doesn't
//      have a `time_s` sample channel at all).
//   4. null → button is shown but disabled with a tooltip explaining why.
const resolveTimeS = (ann, telemetry) => {
  if (ann?.time_s != null && Number.isFinite(ann.time_s)) return ann.time_s;
  if (ann?.distance == null || !telemetry?.laps) return null;
  // Prefer the lap the note was attached to; fall back to lap A.
  const lap = telemetry.laps.find((l) => l.lap_number === ann.lap_number)
            || telemetry.laps[0];
  const ds = lap?.samples?.distance_m;
  const ts = lap?.samples?.time_s;
  if (Array.isArray(ds) && Array.isArray(ts) && ds.length === ts.length && ds.length > 1) {
    let lo = 0, hi = ds.length - 1;
    while (lo < hi - 1) {
      const m = (lo + hi) >> 1;
      if (ds[m] <= ann.distance) lo = m; else hi = m;
    }
    return ts[lo];
  }
  // Last resort: estimate from average speed if available (very rough).
  if (lap?.lap_time && lap?.distance_total_m) {
    const avgSpeed = lap.distance_total_m / lap.lap_time;
    if (avgSpeed > 0) return ann.distance / avgSpeed;
  }
  return null;
};

// Curated set of engineering tags so an engineer can quickly classify what
// happened at that instant. Free-form text is always allowed.
//
// IMPORTANT: this list is the single source of truth for both Replay Studio
// AND the DAQ TelemetryPage annotation UIs. Adding a new tag here surfaces
// it in both places automatically — so the engineer's vocabulary stays
// consistent regardless of where the note is created.
export const ANNOTATION_ICONS = [
  { key: 'oversteer',  label: 'Sobreviraje',     color: '#ef4444', emoji: '↩️' },
  { key: 'understeer', label: 'Subviraje',       color: '#f59e0b', emoji: '↪️' },
  { key: 'lockup',     label: 'Bloqueo',         color: '#a855f7', emoji: '⏹️' },
  { key: 'traction',   label: 'Pérdida tracción',color: '#06b6d4', emoji: '🛞' },
  { key: 'traffic',    label: 'Tráfico',         color: '#fbbf24', emoji: '🚦' },
  { key: 'best',       label: 'Mejor sector',    color: '#22c55e', emoji: '🏁' },
  { key: 'setup',      label: 'Cambio setup',    color: '#94a3b8', emoji: '🔧' },
  { key: 'note',       label: 'Nota',            color: '#60a5fa', emoji: '💬' },
];

// Helper: resolve an annotation to its icon definition (icon overrides),
// falling back to the generic "note" entry. Used to render the same emoji
// + label in every list (Replay panel + DAQ track-side list).
export const iconFor = (ann) => {
  if (!ann) return ANNOTATION_ICONS[ANNOTATION_ICONS.length - 1];
  return ANNOTATION_ICONS.find((i) => i.key === ann.icon)
      || ANNOTATION_ICONS[ANNOTATION_ICONS.length - 1];
};

// ---------- annotation row --------------------------------------------------

function AnnotationRow({ ann, resolvedTime, onSeek, onEdit, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(ann.text);
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const iconDef = iconFor(ann);
  const tint = ann.color || iconDef.color;
  // The button is only navigable when we have a finite time anchor. Surface
  // a disabled state otherwise so the user sees the reason (tooltip).
  const canSeek = Number.isFinite(resolvedTime);

  return (
    <li
      data-testid={`ann-row-${ann.id}`}
      className="group flex items-start gap-2 px-2 py-1.5 rounded hover:bg-neutral-800/60 transition-colors"
    >
      <button
        type="button"
        onClick={() => { if (canSeek) onSeek(resolvedTime); }}
        disabled={!canSeek}
        data-testid={`ann-seek-${ann.id}`}
        title={canSeek
          ? 'Saltar a este instante'
          : 'No se puede saltar: esta nota se creó por distancia y la sesión no tiene muestras de tiempo'}
        className={`font-mono text-[11px] tabular-nums shrink-0 ${
          canSeek ? 'text-gray-300 hover:text-white' : 'text-gray-600 cursor-not-allowed'
        }`}
      >
        <Clock className="w-3 h-3 inline mr-0.5 opacity-60" />
        {formatTime(resolvedTime)}
      </button>
      <span
        className="text-base leading-none shrink-0"
        style={{ color: tint }}
        title={iconDef.label}
      >
        {iconDef.emoji}
      </span>
      {editing ? (
        <>
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { onUpdate(ann.id, { text }); setEditing(false); }
              if (e.key === 'Escape') { setText(ann.text); setEditing(false); }
            }}
            data-testid={`ann-input-${ann.id}`}
            className="h-7 text-xs bg-neutral-900 border-neutral-700"
          />
          <button type="button" onClick={() => { onUpdate(ann.id, { text }); setEditing(false); }} data-testid={`ann-save-${ann.id}`} className="text-green-400 hover:text-green-300">
            <Check className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => { setText(ann.text); setEditing(false); }} data-testid={`ann-cancel-${ann.id}`} className="text-gray-400 hover:text-gray-300">
            <X className="w-4 h-4" />
          </button>
        </>
      ) : (
        <>
          <span className="text-xs text-gray-100 flex-1 break-words">{ann.text}</span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`ann-edit-${ann.id}`}
            className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-blue-400 transition-opacity"
            title="Editar"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(ann.id)}
            data-testid={`ann-delete-${ann.id}`}
            className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity"
            title="Eliminar"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </>
      )}
    </li>
  );
}

// ---------- main component --------------------------------------------------

export default function AnnotationsPanel({
  sessionId, lapNumber, getTime, onSeek, telemetry, onChange,
}) {
  // Annotations live on the telemetry session document. We keep a local copy
  // for snappier UX and refresh from the server on every CRUD operation.
  const [items, setItems] = useState([]);
  const [draftText, setDraftText] = useState('');
  const [draftIcon, setDraftIcon] = useState('note');
  const [creating, setCreating] = useState(false);

  // Sync from parent-provided telemetry doc. Also resolve a usable `time_s`
  // for every annotation so distance-only notes (created from DAQ) still
  // navigate the video correctly. Resolution is memoised against the
  // telemetry doc — only recomputed when laps/samples change.
  useEffect(() => {
    const all = telemetry?.annotations || [];
    // Filter to the current lap (when one is selected) so the engineer sees
    // only relevant notes; the others remain on disk.
    const filtered = lapNumber != null
      ? all.filter((a) => a.lap_number == null || a.lap_number === lapNumber)
      : all;
    // Decorate every entry with `resolved_time` so the row component can
    // render it without re-doing the binary search on every paint.
    const decorated = filtered.map((a) => ({
      ...a,
      _resolved_time: resolveTimeS(a, telemetry),
    }));
    // Sort by resolved time (then by distance) — both null-safe. Using
    // Infinity for missing values pushes them to the bottom of the list
    // instead of corrupting the order (which `null - null = NaN` did).
    decorated.sort((a, b) => {
      const ta = Number.isFinite(a._resolved_time) ? a._resolved_time
              : Number.isFinite(a.distance) ? a.distance / 1000
              : Infinity;
      const tb = Number.isFinite(b._resolved_time) ? b._resolved_time
              : Number.isFinite(b.distance) ? b.distance / 1000
              : Infinity;
      return ta - tb;
    });
    setItems(decorated);
  }, [telemetry, lapNumber]);

  const create = async () => {
    if (!sessionId) {
      toast.error('Sin sesión de telemetría asociada al replay');
      return;
    }
    if (!draftText.trim()) {
      toast.error('Escribí el texto de la anotación');
      return;
    }
    const tNow = typeof getTime === 'function' ? getTime() : 0;
    setCreating(true);
    try {
      // Find the distance at this time so legacy chart views (TelemetryPage)
      // can also place the marker. Look it up in the lap that matches.
      let distance = null;
      const lap = telemetry?.laps?.find((l) => l.lap_number === lapNumber)
                || (telemetry?.laps?.[0]);
      if (lap?.samples?.time_s && lap?.samples?.distance_m) {
        const ts = lap.samples.time_s;
        let lo = 0, hi = ts.length - 1;
        while (lo < hi - 1) { const m = (lo + hi) >> 1; if (ts[m] <= tNow) lo = m; else hi = m; }
        distance = lap.samples.distance_m[lo];
      }
      const res = await axios.post(
        `${API}/telemetry/sessions/${sessionId}/annotations`,
        {
          text: draftText.trim(),
          time_s: tNow,
          distance,
          lap_number: lapNumber,
          icon: draftIcon,
          color: ANNOTATION_ICONS.find((i) => i.key === draftIcon)?.color,
        },
        { withCredentials: true },
      );
      toast.success('Anotación guardada');
      setDraftText('');
      onChange?.((prev) => ({
        ...(prev || {}),
        annotations: [...(prev?.annotations || []), res.data.annotation],
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la anotación');
    } finally {
      setCreating(false);
    }
  };

  const update = async (id, patch) => {
    try {
      const res = await axios.patch(
        `${API}/telemetry/sessions/${sessionId}/annotations/${id}`,
        patch,
        { withCredentials: true },
      );
      onChange?.((prev) => ({
        ...(prev || {}),
        annotations: (prev?.annotations || []).map((a) => (a.id === id ? res.data.annotation : a)),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el cambio');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Borrar esta anotación?')) return;
    try {
      await axios.delete(
        `${API}/telemetry/sessions/${sessionId}/annotations/${id}`,
        { withCredentials: true },
      );
      onChange?.((prev) => ({
        ...(prev || {}),
        annotations: (prev?.annotations || []).filter((a) => a.id !== id),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-2" data-testid="annotations-panel">
      <div className="flex items-center gap-2 text-[11px] text-gray-400">
        <MessageSquare className="w-3.5 h-3.5 text-red-500" />
        <span>Anotaciones {lapNumber != null ? `· vuelta ${lapNumber}` : ''}</span>
        <span className="ml-auto font-mono text-gray-500">{items.length}</span>
      </div>

      {/* Creator */}
      <div className="space-y-1 bg-neutral-900/60 border border-neutral-800 rounded p-2">
        <div className="flex flex-wrap gap-1">
          {ANNOTATION_ICONS.map((i) => (
            <button
              key={i.key}
              type="button"
              onClick={() => setDraftIcon(i.key)}
              data-testid={`ann-icon-${i.key}`}
              title={i.label}
              className={`text-base leading-none px-1.5 py-0.5 rounded border transition-colors ${
                draftIcon === i.key
                  ? 'border-white bg-white/10'
                  : 'border-transparent hover:border-neutral-700'
              }`}
              style={{ color: i.color }}
            >
              {i.emoji}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Input
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !creating) create(); }}
            placeholder="Escribí la anotación y presioná Enter…"
            data-testid="ann-draft-input"
            className="h-7 text-xs bg-neutral-900 border-neutral-700"
          />
          <Button
            type="button"
            size="sm"
            onClick={create}
            disabled={creating || !draftText.trim()}
            data-testid="ann-create-btn"
            className="h-7 px-2 bg-red-700 hover:bg-red-600"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        <p className="text-[10px] text-gray-500 leading-tight">
          Se asocia al instante actual del video. Click en el tiempo para volver al punto.
        </p>
      </div>

      {/* List */}
      <ul className="space-y-0.5 max-h-64 overflow-y-auto pr-1" data-testid="annotations-list">
        {items.length === 0 ? (
          <li className="text-[11px] text-gray-600 italic text-center py-3">Sin anotaciones todavía.</li>
        ) : (
          items.map((ann) => (
            <AnnotationRow
              key={ann.id}
              ann={ann}
              resolvedTime={ann._resolved_time}
              onSeek={(t) => onSeek?.(t)}
              onUpdate={update}
              onDelete={remove}
            />
          ))
        )}
      </ul>
    </div>
  );
}
